/**
 * Session-local asset source for ceSDK uploaded files.
 * Stores uploaded assets in memory for browsing within the current session.
 * Loads assets from "Editor Uploads" collection on initialization.
 */

import { AssetDefinition } from "./cesdk-upload-handler";
import {
  getOrCreateEditorUploadsCollection,
  deleteEditorUploadsFile,
} from "./cesdk-editor-collection";
import { getCollectionMedia } from "../api/media-library/get-collections";
import { collectionFileToAssetDefinition } from "./cesdk-asset-converter";

/**
 * Asset source interface matching ceSDK's AssetSource
 * Based on ceSDK v1.53.0 AssetSource interface
 */
export interface AssetSource {
  id: string;
  findAssets: (queryData?: {
    query?: string;
    page?: number;
    perPage?: number;
    tags?: string | string[];
    types?: string[];
    groups?: string[];
  }) => Promise<
    | {
        assets: AssetDefinition[];
        total: number;
        currentPage: number;
        nextPage?: number; // ceSDK expects nextPage, not perPage in result
      }
    | undefined
  >;
  // Optional methods that ceSDK might use
  addAsset?: (asset: AssetDefinition) => Promise<void>;
  removeAsset?: (assetId: string) => Promise<void>;
}

/**
 * Load assets from "Editor Uploads" collection
 */
async function loadAssetsFromCollection(): Promise<AssetDefinition[]> {
  try {
    const collectionId = await getOrCreateEditorUploadsCollection();
    if (!collectionId) {
      console.warn("Editor Uploads collection not found");
      return [];
    }

    const { list } = await getCollectionMedia({
      collectionId,
      page: 1,
      limit: 1000,
      sort: "-created",
      usage: null,
    });

    const assetDefinitions: AssetDefinition[] = [];
    for (const collectionFile of list) {
      try {
        const assetDefinition = collectionFileToAssetDefinition(collectionFile);
        assetDefinitions.push(assetDefinition);
      } catch (error) {
        console.error(
          "Failed to convert collection file to asset definition:",
          error,
          collectionFile
        );
      }
    }

    return assetDefinitions;
  } catch (error) {
    console.error("Failed to load assets from collection:", error);
    // Return empty array on error - editor can still work without previously uploaded assets
    return [];
  }
}

/**
 * In-memory storage for uploaded assets (loaded from collection on init)
 */
const uploadedAssets: AssetDefinition[] = [];

/**
 * Create a filtered asset source that only returns assets of a specific type
 */
export function createFilteredUploadAssetSource(
  filterType: "image" | "video" | "audio"
): AssetSource {
  const baseSource = createUploadAssetSource();
  return {
    ...baseSource,
    id: `sked-uploads-${filterType}`,
    findAssets: async (queryData = {}) => {
      // Always filter by the specified type
      const filteredQueryData = {
        ...queryData,
        types: [filterType],
      };
      return baseSource.findAssets(filteredQueryData);
    },
  };
}

/**
 * Callback to notify CESDK when assets change
 * Set by the editor when initializing
 */
let notifyAssetSourceChanged: (() => void) | null = null;

/**
 * Set the callback to notify CESDK when asset source contents change
 */
export function setAssetSourceChangeNotifier(callback: () => void): void {
  notifyAssetSourceChanged = callback;
}

/**
 * Create and return the upload asset source
 */
export function createUploadAssetSource(): AssetSource {
  const assetSource: AssetSource = {
    id: "sked-uploads",
    findAssets: async (queryData = {}) => {
      try {
        const {
          query: searchQuery = "",
          page = 0,
          perPage = 20,
          tags: tagsParam,
          types = [],
        } = queryData;

        const tags = Array.isArray(tagsParam)
          ? tagsParam
          : tagsParam
            ? [tagsParam]
            : [];

        let filtered = [...uploadedAssets];

        if (searchQuery) {
          const lowerQuery = searchQuery.toLowerCase();
          filtered = filtered.filter(
            (asset) =>
              asset.label?.toLowerCase().includes(lowerQuery) ||
              asset.id.toLowerCase().includes(lowerQuery)
          );
        }

        if (tags.length > 0) {
          filtered = filtered.filter((asset) => {
            const assetTags = Array.isArray(asset.tags) ? asset.tags : [];
            return assetTags.some((tag) => tags.includes(tag));
          });
        }

        if (types.length > 0) {
          filtered = filtered.filter((asset) => {
            // Ensure assetTags is always an array (defensive check)
            const assetTags = Array.isArray(asset.tags) ? asset.tags : [];
            return types.some((type) => {
              if (type === "video") return assetTags.includes("video");
              if (type === "image") return assetTags.includes("image");
              if (type === "audio") return assetTags.includes("audio");
              return false;
            });
          });
        }

        const start = page * perPage;
        const end = start + perPage;
        const paginated = filtered.slice(start, end);

        const hasMorePages = end < filtered.length;
        const nextPage = hasMorePages ? page + 1 : undefined;

        return {
          assets: paginated.map((asset) => ({
            ...asset,
            context: {
              sourceId: "sked-uploads",
            },
            locale: "en",
          })),
          total: filtered.length,
          currentPage: page,
          nextPage: nextPage,
        };
      } catch (error) {
        console.error("findAssets error:", error);
        throw error;
      }
    },
    addAsset: async (asset: AssetDefinition) => {
      addUploadedAsset(asset);
    },
    removeAsset: async (assetId: string) => {
      try {
        // Delete from server first (source of truth)
        await deleteEditorUploadsFile(assetId);

        // Remove from in-memory cache only on success
        const index = uploadedAssets.findIndex((a) => a.id === assetId);
        if (index !== -1) {
          uploadedAssets.splice(index, 1);
        }

        // Notify CESDK that asset source contents have changed to refresh the UI
        if (notifyAssetSourceChanged) {
          notifyAssetSourceChanged();
        }
      } catch (error) {
        console.error("Failed to remove asset:", error);
        throw error; // Propagate to CESDK UI
      }
    },
  };

  return assetSource;
}

/**
 * Add an uploaded asset to the session storage (in-memory cache only).
 * Assets are already persisted in the collection, so we just cache them for the session.
 *
 * IMPORTANT: CESDK sometimes calls addAsset with corrupted data (arrays/strings become objects).
 * We skip updates if the asset already exists to preserve our correctly-formatted data.
 *
 * New uploads are prepended (added to the beginning) so they appear first in the list.
 */
export function addUploadedAsset(asset: AssetDefinition): void {
  // Check if asset already exists (by ID)
  const existingIndex = uploadedAssets.findIndex((a) => a.id === asset.id);
  if (existingIndex === -1) {
    // Only add if tags is a proper array (skip corrupted CESDK data)
    if (!Array.isArray(asset.tags)) {
      return;
    }
    // Prepend new uploads so they appear first in the list
    uploadedAssets.unshift(asset);
  }
  // Don't overwrite existing assets - CESDK may pass corrupted data
  // No need to save to localStorage - assets persist in collection
}

/**
 * Clear all uploaded assets from in-memory cache (called when explicitly needed)
 */
export function clearUploadedAssets(): void {
  uploadedAssets.length = 0;
  // No need to clear localStorage - assets persist in collection
}

/**
 * Get all uploaded assets
 */
export function getUploadedAssets(): AssetDefinition[] {
  return [...uploadedAssets];
}

/**
 * Initialize uploaded assets from collection (called when editor opens)
 * This is async and should be awaited before creating the asset source.
 */
export async function initializeUploadedAssets(): Promise<void> {
  try {
    const loaded = await loadAssetsFromCollection();
    uploadedAssets.length = 0;
    uploadedAssets.push(...loaded);
  } catch (error) {
    console.error("Failed to initialize uploaded assets:", error);
    uploadedAssets.length = 0;
  }
}
